// sermonSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for fetching sermons by verse ID
export const fetchSermons = createAsyncThunk('sermons/fetchSermons', async (verseId, thunkAPI) => {
  try {
    const response = await fetch(`/api/sermon/${verseId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sermons');
    }

    const data = await response.json();
    return data; // Return data to the reducer
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

// Async thunk for adding a new sermon
export const addSermon = createAsyncThunk(
  'sermons/addSermon',
  async ({ verseId, ...sermonData }, thunkAPI) => {
    try {
      const response = await fetch(`/api/sermon/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verseId,
          ...sermonData,
        }), // Send all sermon data directly
      });

      if (!response.ok) {
        throw new Error('Failed to add sermon');
      }

      const data = await response.json();
      return data; // Return response data to the reducer
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
  
  // Async thunk for updating an existing sermon
  export const updateSermon = createAsyncThunk(
    'sermons/updateSermon',
    async ({ sermonId, ...sermonData }, thunkAPI) => {
      try {
        const response = await fetch(`/api/sermon/${sermonId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sermonData), // Send updated sermon data
        });
  
        if (!response.ok) {
          throw new Error('Failed to update sermon');
        }
  
        const data = await response.json();
        return data; // Return response data to the reducer
      } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
      }
    }
  );
  
  // Async thunk for deleting a sermon
  export const deleteSermon = createAsyncThunk(
    'sermons/deleteSermon',
    async (sermonId, thunkAPI) => {
      try {
        const response = await fetch(`/api/sermon/${sermonId}`, {
          method: 'DELETE',
        });
  
        if (!response.ok) {
          throw new Error('Failed to delete sermon');
        }
  
        return { sermonId }; // Return deleted sermon ID to the reducer
      } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
      }
    }
  );


// Thunk to fetch all sermons for the logged-in user
export const fetchUserSermons = createAsyncThunk(
  'sermons/fetchUserSermons',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/sermon/user');
      if (!response.ok) {
        throw new Error('Failed to fetch sermons');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch sermon by ID
export const fetchSermonById = createAsyncThunk(
  'sermons/fetchSermonById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/sermon/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch sermon');
      }
      return await response.json(); // Ensure API returns sermon as an object
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

  // Async thunk to fetch sermons by chapter and book
export const fetchSermonsByChapter = createAsyncThunk(
  'sermons/fetchSermonsByChapter',
  async ({ book, chapter }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({ book, chapter }).toString();
      const response = await fetch(`/api/sermon/user/chapter?${queryParams}`, {
        method: 'GET', // Add the method explicitly
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      return data
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update sermon by ID
export const updateSermonById = createAsyncThunk(
  'sermons/updateSermonById',
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/sermons/${id}`, {
        method: 'PUT', // Ensure the API supports this method
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData),
      });
      if (!response.ok) {
        throw new Error('Failed to update sermon');
      }
      return await response.json(); // Ensure API returns updated sermon
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
 

const sermonSlice = createSlice({
  name: 'sermons',
  initialState: {
    sermons: {}, // Stores sermons by verseId
    userSermons: [],
    selectedSermon: null,
    loading: false,
    status: 'idle', // Add status to initialState
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetching sermons
      .addCase(fetchSermons.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSermons.fulfilled, (state, action) => {
        const { verseId, sermons } = action.payload; // Ensure API returns this structure
        state.sermons[verseId] = sermons;
        state.loading = false;
      })
      .addCase(fetchSermons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle adding a sermon
      .addCase(addSermon.fulfilled, (state, action) => {
        const { verseId, newSermon } = action.payload; // Ensure API returns this structure
        if (!state.sermons[verseId]) {
          state.sermons[verseId] = [];
        }
        state.sermons[verseId].push(newSermon);
      })

      // Handle updating a sermon
      .addCase(updateSermon.fulfilled, (state, action) => {
        const updatedSermon = action.payload;
        // console.log("updatedSermon", updatedSermon)
        const sermons = state.sermons[updatedSermon.verseId] || [];
        const index = sermons.findIndex((sermon) => sermon?.id === updatedSermon.id);
        if (index !== -1) {
          sermons[index] = updatedSermon;
        }
      })

      // Handle deleting a sermon
      .addCase(deleteSermon.fulfilled, (state, action) => {
        const { sermonId } = action.payload;

        state.userSermons = (state.userSermons || []).filter(
          (sermon) => sermon.id !== sermonId
        );
    
        // Debugging: Log the current sermons state before deletion
        console.log('Sermons state before deletion:', state.sermons);
    
        Object.keys(state.sermons).forEach((verseId) => {
            // Ensure state.sermons[verseId] is an array
            if (Array.isArray(state.sermons[verseId])) {
                const filteredSermons = state.sermons[verseId].filter(
                    (sermon) => sermon && sermon.id !== sermonId
                );
    
                if (filteredSermons.length > 0) {
                    state.sermons[verseId] = filteredSermons;
                } else {
                    delete state.sermons[verseId]; // Remove the verseId if no sermons remain
                }
            } else {
                console.warn(`state.sermons[${verseId}] is not an array.`);
            }
        });
    
    })
    .addCase(deleteSermon.rejected, (state, action) => {
        state.error = action.payload;
    })
    

      // Handle fetching sermons by chapter
      .addCase(fetchSermonsByChapter.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSermonsByChapter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.sermons = { ...state.sermons, ...action.payload }; // Merge new data with existing state
      })
      .addCase(fetchSermonsByChapter.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch sermons';
      })
      .addCase(fetchUserSermons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserSermons.fulfilled, (state, action) => {
        state.loading = false;
        state.userSermons = action.payload;
      })
      .addCase(fetchUserSermons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSermonById.pending, (state) => {
        state.loading = true;
        state.selectedSermon = null;
      })
      .addCase(fetchSermonById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSermon = action.payload; // Store the fetched sermon
      })
      .addCase(fetchSermonById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update a sermon
      .addCase(updateSermonById.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSermonById.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSermon = action.payload;
        const sermons = state.sermons[updatedSermon.verseId] || [];
        const index = sermons.findIndex((sermon) => sermon.id === updatedSermon.id);
        if (index !== -1) {
          sermons[index] = updatedSermon; // Update the sermon in the state
        }
      })
      .addCase(updateSermonById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
      
  },
});


export default sermonSlice.reducer;
